/**
 * ReportPageV2 — xuất biên bản / báo cáo từ file điểm .xlsx.
 *
 * Hai loại:
 *  - 'bbth'     Biên bản tổng hợp kết quả kiểm tra (kèm ảnh chân dung)
 *  - 'practice' Báo cáo quá trình đào tạo thực hành trên sân tập lái
 *
 * Vì sao sinh file ở BE chứ không ở trình duyệt: ảnh chân dung nằm trên
 * S3 với ACL=private và bucket không mở CORS, nên trình duyệt hiển thị
 * được ảnh (<img src>) nhưng KHÔNG đọc được bytes để nhúng vào Word.
 * BE lấy ảnh rồi ghép luôn, FE chỉ upload và tải file về.
 */
import { useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import datV2 from '../../services/datV2';
import V2PageLayout from '../../components/v2/V2PageLayout';
import { ROLES, hasAnyRole } from '../../utils/auth';

const ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Cột bắt buộc theo từng loại — hiện ra để người dùng tự đối chiếu trước
// khi upload, thay vì đợi BE báo lỗi.
const KINDS = {
  bbth: {
    label: 'Biên bản tổng hợp kết quả kiểm tra',
    required: ['Tên khách hàng', 'ĐIỂM HẾT MÔN HÌNH', 'ĐIỂM HẾT MÔN ĐƯỜNG'],
    blanks: 'SBD, ngày cấp và nơi cấp CCCD không có trong file điểm nên để '
      + 'trống, điền tay sau khi in. Ô kết luận Đạt / Không đạt in hai ô '
      + 'vuông trống để giáo viên tự đánh dấu.',
  },
  practice: {
    label: 'Báo cáo quá trình đào tạo thực hành',
    required: ['Tên khách hàng', 'Mã học viên - theo CSDL'],
    blanks: 'Bảy nội dung đào tạo (mục 1–7) lấy từ các cột "TH - ..." trong '
      + 'file điểm. Cột nào cũng trống thì in một ô gộp để điền tay; có số '
      + 'thì tách thành bảy ô riêng. Tổng thời gian học (mục 8) và tổng '
      + 'quãng đường (mục 9) in dòng chấm để điền tay, không lấy số liệu '
      + 'DAT nữa. Hạng đào tạo (mục 5) và khóa học (mục 6) tự điền theo '
      + 'cột "Khóa học" của file, thiếu thì lấy theo dữ liệu học viên '
      + 'trong hệ thống.',
  },
};

export default function ReportPageV2() {
  const [kind, setKind] = useState('bbth');
  const [file, setFile] = useState(null);
  const [withPortrait, setWithPortrait] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  // Role được lấy ảnh chân dung — phải khớp PORTRAIT_ROLES ở
  // uniapp-backend (dat-session-v2.controller.js). Lệch danh sách thì
  // người dùng bấm xuất mới nhận 403, thay vì bị tắt ô từ đầu.
  // REPORTER (13) được thêm 09/2026 theo yêu cầu nghiệp vụ.
  const canPortrait = hasAnyRole(
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.DRIVING_ADMIN_NEW,
    ROLES.DRIVING_REPORTER,
  );

  const cfg = KINDS[kind];

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setResult(null);
    if (f && !/\.xlsx$/i.test(f.name)) {
      toast.error('Chỉ nhận file .xlsx');
      e.target.value = '';
      setFile(null);
      return;
    }
    setFile(f);
  };

  const onExport = async () => {
    if (!file || busy) return;
    setBusy(true);
    setResult(null);
    // Khai báo ngoài try để nhánh catch còn đọc được khi dựng thông báo lỗi.
    const wantPortrait = kind === 'bbth' && withPortrait && canPortrait;
    try {
      // Báo cáo thực hành không còn in tổng giờ / tổng km (mục 8-9 để
      // trung tâm điền tay) nên nguồn số liệu DAT không còn ảnh hưởng gì
      // tới file xuất ra — bỏ ô chọn nguồn, luôn gửi 'file' để BE khỏi tra
      // từng học viên cho một kết quả không dùng đến. Muốn bật lại mục 8-9
      // thì trả ô chọn này về (BE vẫn nhận `source`).
      const res = kind === 'bbth'
        ? await datV2.exportBbthReport(file, wantPortrait)
        : await datV2.exportPracticeReport(file, 'file');

      // Tải file về máy người dùng.
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Thu hồi sau một nhịp để trình duyệt kịp bắt đầu tải.
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      setResult({
        students: res.studentCount,
        portraits: res.portraitCount,
        withPortrait: wantPortrait,
        kind,
      });
      toast.success(`Đã xuất ${res.studentCount || ''} bản`.trim());
    } catch (err) {
      let msg = err?.response?.data?.message || err?.message
        || 'Xuất file thất bại.';
      // 403 khi đang bật "kèm ảnh" gần như luôn là do danh sách role được
      // lấy ảnh ở BE chưa khớp với FE. Nói rõ cách đi tiếp thay vì để
      // người dùng nhìn lỗi trống không.
      if (err?.response?.status === 403 && wantPortrait) {
        msg += ' Bỏ chọn "kèm ảnh chân dung" để xuất bản không có ảnh.';
      }
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <V2PageLayout title='Xuất biên bản / báo cáo' gridClassName=''>
      <div style={{ maxWidth: 760 }}>
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold'>Loại văn bản</Form.Label>
          <Form.Select
            value={kind}
            disabled={busy}
            onChange={(e) => { setKind(e.target.value); setResult(null); }}
          >
            {Object.entries(KINDS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold'>File điểm (.xlsx)</Form.Label>
          <Form.Control
            ref={inputRef}
            type='file'
            accept={ACCEPT}
            onChange={onPick}
            disabled={busy}
          />
          <Form.Text muted>
            Đọc theo tên cột, không theo thứ tự. Cần có:{' '}
            {cfg.required.map((c) => <code key={c} className='me-2'>{c}</code>)}
          </Form.Text>
        </Form.Group>

        {kind === 'bbth' && (
          <Form.Group className='mb-3'>
            <Form.Check
              type='checkbox'
              id='withPortrait'
              label='Kèm ảnh chân dung học viên'
              checked={withPortrait && canPortrait}
              disabled={!canPortrait || busy}
              onChange={(e) => setWithPortrait(e.target.checked)}
            />
            {!canPortrait && (
              <Form.Text className='text-warning'>
                Tài khoản của bạn không có quyền lấy ảnh chân dung — biên bản
                vẫn xuất được, ô ảnh để trống.
              </Form.Text>
            )}
          </Form.Group>
        )}

        <div className='d-flex gap-2 mb-3'>
          <Button onClick={onExport} disabled={!file || busy}>
            {busy && <Spinner as='span' size='sm' className='me-2' />}
            {busy ? 'Đang xuất…' : 'Xuất file Word'}
          </Button>
          {(file || result) && (
            <Button variant='outline-secondary' onClick={onReset} disabled={busy}>
              Chọn lại
            </Button>
          )}
        </div>

        {busy && (
          <Alert variant='light' className='border'>
            Đang đọc file và ghép văn bản. Với vài trăm học viên có thể mất
            một lúc — đừng đóng tab.
          </Alert>
        )}

        {result?.error && <Alert variant='danger'>{result.error}</Alert>}

        {result && !result.error && (
          <Alert variant='success'>
            <div>Đã xuất <strong>{result.students}</strong> bản.</div>
            {result.kind === 'bbth' && result.withPortrait && (
              <div className='small mt-1'>
                {result.portraits}/{result.students} học viên có ảnh chân dung.
                {result.portraits < result.students
                  && ' Số còn lại chưa có ảnh trong hệ thống nên để trống.'}
              </div>
            )}
          </Alert>
        )}

        <hr />
        <div className='small text-muted'>
          <div className='fw-semibold mb-1'>Ô để trống</div>
          {cfg.blanks}
        </div>
      </div>
    </V2PageLayout>
  );
}
